<template>
  <v-row>
    <v-col cols="12" class="header-with-select-box">
      <h3 class="text-primary weight-600">Upload Documents</h3>
    </v-col>
    <v-col cols="12">
      <v-row>
        <v-col cols="12">
          <v-row v-for="(supportingDoc, index) in supplementalDocs" :key="index" class="mb-4 pa-2 border rounded-16">
            <v-col cols="12" sm class="d-flex align-center">
              <v-text-field
                :id="`supporting-doc-label-${index}`"
                v-model="supportingDoc.label"
                placeholder="Add label to your upload"
                class="lh--force-white-background"
                hide-details />
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex align-center">
              <div v-if="supportingDoc.links.length" class="w-100">
                <div v-for="link in supportingDoc.links" :key="link" class="d-flex align-center flex-wrap">
                  <v-btn :href="link" target="_blank" variant="text" color="primary" prepend-icon="mdi-download">
                    Download
                  </v-btn>
                  <span v-if="captionFor(link)" class="text-caption text-medium-emphasis ml-2">
                    {{ captionFor(link) }}
                  </span>
                </div>
              </div>
              <div v-else-if="props.isEditable" class="w-100">
                <ChartUploadFileComponent
                  v-if="props.cmmBudgeted"
                  :store-var="props.downloadDestination"
                  :store-var-index="index"
                  :other-links="props.otherLinks"
                  @download-link="addFreeFormSupplementalDocsDownloadLink" />
                <UploadFileComponent
                  v-else
                  :download-destination="props.downloadDestination"
                  :download-destination-index="index"
                  @download-link="addFreeFormSupplementalDocsDownloadLink" />
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex align-center flex-wrap ga-2">
          <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="addSupportingDocumentUpload">
            Add Supplemental Document
          </v-btn>
          <!-- One paste entry point for the whole list: each paste fills the first empty row, else adds one. -->
          <UploadFileComponent
            v-if="props.isEditable"
            paste-only
            allow-paste
            :download-destination="props.downloadDestination"
            :download-destination-index="pasteTargetIndex"
            @download-link="addFreeFormSupplementalDocsDownloadLink" />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { computed, toRef } from "vue";
import ChartUploadFileComponent from "@/components/ChartUploadFileComponent.vue";
import UploadFileComponent from "@/components/UploadFileComponent";
import { usePreparedInfo } from "@/composables/usePreparedInfo";

const emit = defineEmits(["add-free-form-supplemental-docs"]);

const props = defineProps({
  supplementalDocs: {
    type: Array,
    required: true,
  },
  downloadDestination: {
    type: String,
    default() {
      return "";
    },
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  cmmBudgeted: {
    type: Boolean,
    default() {
      return false;
    },
  },
  otherLinks: {
    type: Array,
    default() {
      return [];
    },
  },
});

// toRef, not ref(props.x): the parent replaces the array when it merges these into the patient
// chart, and a captured copy would keep rendering the old rows.
const supplementalDocs = toRef(props, "supplementalDocs");
const { remember, captionFor } = usePreparedInfo();

const pasteTargetIndex = computed(() => {
  const empty = supplementalDocs.value.findIndex((doc) => !doc.links?.length);
  return empty === -1 ? supplementalDocs.value.length : empty;
});

function addSupportingDocumentUpload() {
  supplementalDocs.value.push({
    label: null,
    links: [],
  });
}

const addFreeFormSupplementalDocsDownloadLink = (downloadLinkInfo) => {
  remember(downloadLinkInfo.link, downloadLinkInfo);
  emit("add-free-form-supplemental-docs", downloadLinkInfo);
};
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";
</style>
